'use strict';

/**
 * The ONE email-address rule (L-R7CLOSE row 43).
 *
 * An address is judged by its SHAPE and the RFC 5321 lengths — never against a bundled top-level-domain
 * list. Joi's default `.email()` checks the TLD against the IANA list it ships, which refuses the reserved
 * `.local` every isolated suite uses (TII-001) and every real TLD newer than the snapshot; the fleet held
 * 65 such checks beside six shape-judged ones and 55 files of hand-written regexes, three answers to one
 * question.
 *
 * This is a PORT of Joi's own engine (`@hapi/address` 5.1.1: `validateEmail` + `analyzeDomain`) with its
 * options fixed at the shape-judged values: no TLD list, at least two domain labels, unicode allowed,
 * lengths enforced. It is ported rather than imported because `@hapi/address` requires Node's `url` and
 * `util`, which no browser or React Native bundle carries. The port reads only `URL` (for a domain's
 * punycode form); UTF-8 octets are computed, because jsdom — every web app's test environment — has no
 * `TextEncoder`. `tests/unit/emailAddress.parity.test.js` judges it against Joi's
 * `email({ tlds: false })` verdict so the two cannot drift.
 *
 * Not supported, exactly as upstream: quoted local parts, address literals (`a@[127.0.0.1]`), comments.
 *
 * Portions derived from `@hapi/address` 5.1.1 — Copyright (c) 2019-2022, Sideway, Inc. and Project
 * contributors, used under the BSD-3-Clause licence, whose full text and conditions travel with this
 * package in `LICENSE-hapi-address`.
 *
 * @module @puffin-core/email-address/emailAddress
 */

/** The RFC 5321 limits the rule enforces. */
const EMAIL_ADDRESS_LIMITS = Object.freeze({ localPartOctets: 64, addressLength: 254 });

/** The fewest domain labels an address may have (`example.com`; never a bare `localhost`). */
const MIN_DOMAIN_SEGMENTS = 2;
const MAX_DOMAIN_LENGTH = 256;
const MAX_LABEL_LENGTH = 63;

/** Any UTF-16 code unit above ASCII (a surrogate half included), i.e. a non-ASCII character. */
const NON_ASCII_RX = /[\u0080-\uffff]/;
/** RFC 5321 atext (`_` rides in `\w`). */
const ATEXT_RX = /^[\w!#$%&'*+\-/=?^`{|}~]+$/;
/** RFC 6531 UTF8-non-ascii, matched over a character's UTF-8 octets written as a binary string. */
const ATOM_RX = new RegExp(
  [
    '(?:[\\xc2-\\xdf][\\x80-\\xbf])',
    '(?:\\xe0[\\xa0-\\xbf][\\x80-\\xbf])|(?:[\\xe1-\\xec][\\x80-\\xbf]{2})|(?:\\xed[\\x80-\\x9f][\\x80-\\xbf])|(?:[\\xee-\\xef][\\x80-\\xbf]{2})',
    '(?:\\xf0[\\x90-\\xbf][\\x80-\\xbf]{2})|(?:[\\xf1-\\xf3][\\x80-\\xbf]{3})|(?:\\xf4[\\x80-\\x8f][\\x80-\\xbf]{2})',
  ].join('|'),
);
/** The separators no domain may hold (control characters and space are judged by code, below). */
const DOMAIN_SEPARATOR_RX = /[@:/\\#!$&'()*+,;=?]/;
/** The highest code point that is a control character or space (U+0000–U+0020). */
const LAST_CONTROL_OR_SPACE = 0x20;
const TLD_SEGMENT_RX = /^[a-zA-Z](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const DOMAIN_SEGMENT_RX = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;

/** A lone surrogate has no UTF-8 form; the WHATWG encoder writes U+FFFD in its place, as upstream's does. */
const REPLACEMENT_OCTETS = Object.freeze([0xef, 0xbf, 0xbd]);

/**
 * One code point's UTF-8 octets — computed, not read from a `TextEncoder`: jsdom (every web app's test
 * environment) carries none, and upstream only reads the octets. Byte-exact with the WHATWG encoder,
 * lone surrogates included (proved by the parity suite).
 *
 * @param {string} char - one code point, as `for…of` over a string yields it
 * @returns {number[]}
 */
function utf8Octets(char) {
  const codePoint = char.codePointAt(0);
  if (codePoint < 0x80) return [codePoint];
  if (codePoint < 0x800) return [0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f)];
  if (codePoint >= 0xd800 && codePoint <= 0xdfff) return [...REPLACEMENT_OCTETS];
  if (codePoint < 0x10000) {
    return [0xe0 | (codePoint >> 12), 0x80 | ((codePoint >> 6) & 0x3f), 0x80 | (codePoint & 0x3f)];
  }
  return [
    0xf0 | (codePoint >> 18),
    0x80 | ((codePoint >> 12) & 0x3f),
    0x80 | ((codePoint >> 6) & 0x3f),
    0x80 | (codePoint & 0x3f),
  ];
}

/** A string's length in UTF-8 octets (RFC 5321 counts octets, not characters). */
function utf8Length(text) {
  let octets = 0;
  for (const char of text) octets += utf8Octets(char).length;
  return octets;
}

/** A character's UTF-8 octets as a binary string, for `ATOM_RX`. */
function toBinary(char) {
  return String.fromCharCode(...utf8Octets(char));
}

/**
 * Is every dot-separated segment of the local part a non-empty atom?
 *
 * @param {string} localPart
 * @param {boolean} ascii - the whole address is ASCII (upstream's fast path)
 * @returns {boolean}
 */
function isValidLocalPart(localPart, ascii) {
  for (const segment of localPart.split('.')) {
    if (!segment.length) return false;
    if (ascii) {
      if (!ATEXT_RX.test(segment)) return false;
      continue;
    }
    for (const char of segment) {
      if (!ATEXT_RX.test(char) && !ATOM_RX.test(toBinary(char))) return false;
    }
  }
  return true;
}

/**
 * Does the domain hold a control character, a space or a URL separator? (Upstream's
 * `DOMAIN_CONTROL_RX`, with the control range judged by code point.)
 *
 * @param {string} domain
 * @returns {boolean}
 */
function hasControlOrSeparator(domain) {
  if (DOMAIN_SEPARATOR_RX.test(domain)) return true;
  for (let i = 0; i < domain.length; i += 1) {
    if (domain.charCodeAt(i) <= LAST_CONTROL_OR_SPACE) return true;
  }
  return false;
}

/**
 * A domain in its ASCII (punycode) form, as the platform's `URL` renders its host — or unchanged when
 * the platform cannot (a React Native `URL` without `host`), which then fails the ASCII label rules.
 *
 * @param {string} domain
 * @returns {string}
 */
function toAsciiDomain(domain) {
  const escaped = domain.includes('%') ? domain.replace(/%/g, '%25') : domain;
  try {
    return new URL(`http://${escaped}`).host;
  } catch {
    return escaped;
  }
}

/**
 * Is the domain at least two labels, each 1–63 characters of letters, digits and inner hyphens, the
 * last starting with a letter?
 *
 * @param {string} domain
 * @returns {boolean}
 */
function isValidDomain(domain) {
  if (!domain || domain.length > MAX_DOMAIN_LENGTH) return false;
  const normalized = NON_ASCII_RX.test(domain) ? domain.normalize('NFC') : domain;
  if (hasControlOrSeparator(normalized)) return false;
  const segments = toAsciiDomain(normalized).split('.');
  if (segments.length < MIN_DOMAIN_SEGMENTS) return false;
  return segments.every((segment, index) => {
    if (!segment.length || segment.length > MAX_LABEL_LENGTH) return false;
    const rule = index < segments.length - 1 ? DOMAIN_SEGMENT_RX : TLD_SEGMENT_RX;
    return rule.test(segment);
  });
}

/**
 * Is `value` an email address — judged by its shape and the RFC 5321 lengths, on any top-level domain?
 * The value is judged as given: a caller trims first (the Joi builder does).
 *
 * @param {*} value
 * @returns {boolean} false for anything that is not a string holding an address; never throws
 */
function isEmailAddress(value) {
  if (typeof value !== 'string' || !value) return false;
  const ascii = !NON_ASCII_RX.test(value);
  const address = ascii ? value : value.normalize('NFC');
  const parts = address.split('@');
  if (parts.length !== 2) return false;
  const [localPart, domain] = parts;
  if (!localPart) return false;
  if (address.length > EMAIL_ADDRESS_LIMITS.addressLength) return false;
  if (utf8Length(localPart) > EMAIL_ADDRESS_LIMITS.localPartOctets) return false;
  return isValidLocalPart(localPart, ascii) && isValidDomain(domain);
}

/**
 * The API tier's schema for an email address over the ONE rule: a trimmed string of at most 254
 * characters that `isEmailAddress` accepts, refused as Joi's own `string.email`. Required-ness and
 * empties are the caller's (`.required()`, `.allow(null, '')`).
 *
 * @param {{ joi: import('joi') }} options - the host's Joi, injected (the `joiPrefixedId` precedent)
 * @returns {import('joi').StringSchema}
 */
function joiEmailAddress({ joi } = {}) {
  if (!joi || typeof joi.string !== 'function') {
    throw new Error('joiEmailAddress requires { joi } — pass the host`s Joi');
  }
  return joi
    .string()
    .trim()
    .max(EMAIL_ADDRESS_LIMITS.addressLength)
    .custom(
      (value, helpers) => (isEmailAddress(value) ? value : helpers.error('string.email')),
      'the ONE email-address rule',
    );
}

module.exports = { EMAIL_ADDRESS_LIMITS, isEmailAddress, joiEmailAddress };
